import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext";
import { ToastContext } from "../../contexts/ToastContext";
import { apiFetch } from "../../services/api";
import { formatMoney } from "../../utils/formatters";
import { normalizeCategories } from "../../utils/categoryHelpers";
import { useAuthedApi } from "../../hooks/useAuthedApi";

export default function AdminConsolePage() {
  const navigate = useNavigate();
  const { user, token } = useContext(AuthContext);
  const toast = useContext(ToastContext);
  const adminTheme = {
    ink: "#111110",
    muted: "#888780",
    border: "rgba(0,0,0,0.06)",
    surface: "#FBFAF8",
    gold: "#C49A3C",
    goldSoft: "#F5EDD6",
    danger: "#A94442",
  };
  const [tab, setTab] = useState("products");
  const [reloadKey, setReloadKey] = useState(0);
  const [busy, setBusy] = useState(false);

  {/*  - Draggable Popup Modal when editing  */}
  const DraggableModal = ({ title, children, onClose }) => {
  const [pos, setPos] = useState({ x: 200, y: 100 });
  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const startDrag = (e) => {
    setDragging(true);
    setOffset({
      x: e.clientX - pos.x,
      y: e.clientY - pos.y,
    });
  };

  const onDrag = (e) => {
    if (!dragging) return;
    setPos({
      x: e.clientX - offset.x,
      y: e.clientY - offset.y,
    });
  };

  const stopDrag = () => setDragging(false);

  return (
    <div
      onMouseMove={onDrag}
      onMouseUp={stopDrag}
      style={{
        position: "fixed",
        left: pos.x,
        top: pos.y,
        width: 420,
        background: "#fff",
        borderRadius: 12,
        border: "1px solid rgba(0,0,0,0.06)",
        boxShadow: "0 12px 32px rgba(0,0,0,0.05)",
        zIndex: 9999,
      }}
    >
      {/* HEADER (DRAG HANDLE) */}
      <div
        onMouseDown={startDrag}
        style={{
          padding: 10,
          cursor: "move",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <strong>{title}</strong>
        <button onClick={onClose}>✕</button>
      </div>

      <div style={{ padding: 15 }}>{children}</div>
    </div>
  );
};
const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid rgba(0,0,0,0.06)",
};

  const { data: productsData } = useAuthedApi(
    user?.role === "admin" ? `/admin/products?limit=80` : null,
    token,
    [reloadKey],
  );
  const { data: categoriesData } = useAuthedApi(
    user?.role === "admin" ? `/admin/categories` : null,
    token,
    [reloadKey],
  );
  const { data: setsData } = useAuthedApi(
  user?.role === "admin" ? `/kits` : null,
  token,
  [reloadKey],
);
  const { data: configsData } = useAuthedApi(
    user?.role === "admin" ? `/admin/configurations` : null,
    token,
    [reloadKey],
  );
  const { data: masterData } = useAuthedApi(
    user?.role === "admin" ? `/admin/master-data` : null,
    token,
    [reloadKey],
  );
  const { data: brandsData } = useAuthedApi(
  user?.role === "admin" ? `/brands` : null,
  token,
  [reloadKey],
);

  const [productForm, setProductForm] = useState({
  name: "",
  slug: "",
  price: "",
  stock: "0",
  category_id: "",
  set_id: "",
  brand_id: "",
  kit_role: "",
  description: "",
  is_featured: false,
  is_active: true,
  ai_keywords: "",
});
  const [productImage, setProductImage] = useState(null);
  const [setImage, setSetImage] = useState(null);
  const KIT_ROLES = [
  "Soil & Compost",
  "Seeds & Plants",
  "Fertiliser",
  "Tools",
  "Pest Control",
];
const ROLE_ORDER = [
  "Soil & Compost",
  "Seeds & Plants",
  "Fertiliser",
  "Tools",
  "Pest Control",
];

const getSetProgress = (setId) => {
  const items = products.filter(
    (p) => Number(p.set_id) === Number(setId)
  );

  const completed = ROLE_ORDER.filter((role) =>
    items.some(
  (p) =>
    (p.role || p.kit_role) === role
)
  );

  return {
    completed: completed.length,
    roles: completed,
  };
};
{/* Admin Console - Set Form */}
const [setForm, setSetForm] = useState({
  name: "",
  category_id: "",
  brand_id: "",
  total_price: "",
  short_description: "",
  is_featured: false,
  is_active: true,
  product_ids: [],
});
{/* Admin Console - Brand Form */}
const [brandForm, setBrandForm] = useState({
  name: "",
  slug: "",
  logo_url: "",
  description: "",
});
const [brandLogo, setBrandLogo] = useState(null);

{/* Auto-generate Slug */}
const generateSlug = (name) =>
  name
     .toLowerCase()
    .trim()
    .replace(/['"]/g, "")          // remove quotes/apostrophes
    .replace(/[^a-z0-9\s-]/g, "")  // remove special chars
    .replace(/\s+/g, "-")          // spaces -> hyphen
    .replace(/-+/g, "-")           // collapse multiple hyphens
    .replace(/^-|-$/g, "");        // trim leading/trailing hyphens
    

{/* Admin Console - Category Form */}
  const [categoryForm, setCategoryForm] = useState({
    name: "",
    slug: "",
    sort_order: "0",
  });
const [categoryImage, setCategoryImage] = useState(null);
const [categoryImageUrl, setCategoryImageUrl] = useState("");
const uploadCategoryImage = async (file, categoryId) => {
  const formData = new FormData();
  formData.append("category_id", categoryId);
  formData.append("file", file);

  const res = await apiFetch(
    "/admin/upload/category-image",
    {
      method: "POST",
      body: formData,
    },
    token ? { authToken: token } : {}
  );

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(data.error || data.detail || "Upload failed");
  }

  return data;
};

const uploadProductImage = async (file, productId) => {
  const formData = new FormData();
  formData.append("product_id", productId);
  formData.append("file", file);

  const res = await apiFetch(
    "/admin/upload/product-image",
    {
      method: "POST",
      body: formData,
    },
    token ? { authToken: token } : {}
  );

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(
      data.detail?.[0]?.msg || data.detail || "Upload failed"
    );
  }

  return data;
};
const uploadSetImage = async (file, setId) => {
  const formData = new FormData();
  formData.append("file", file);

  const res = await apiFetch(
    `/admin/kits/${setId}/image`,
    {
      method: "POST",
      body: formData,
    },
    {
      authToken: token,
    }
  );

  const body = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(body.detail || "Image upload failed");
  }

  return body;
};
const uploadBrandLogo = async (file, brandId) => {
  if (!token) throw new Error("No auth token");

  const formData = new FormData();
  formData.append("brand_id", brandId);
  formData.append("file", file);

  const res = await apiFetch(
    "/admin/brands",
    {
      method: "POST",
      body: formData,
    },
    token ? { authToken: token } : {}
  );

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(data.detail || data.error || "Upload failed");
  }

  return data;
};
const [editModalOpen, setEditModalOpen] = useState(false);
const [editingCategory, setEditingCategory] = useState(null);
const [editForm, setEditForm] = useState({
  name: "",
  description: "",
  is_active: true,
});
const openEditCategory = (category) => {
  setEditingCategory(category);
  setEditForm({
    name: category.name || "",
    description: category.description || "",
    is_active: category.is_active ?? true,
  });
  setEditModalOpen(true);
};

  const [configForm, setConfigForm] = useState({
    key: "",
    value: "{}",
    description: "",
    is_sensitive: false,
  });
  const [masterForm, setMasterForm] = useState({
    domain: "",
    key_name: "",
    value_json: "{}",
    description: "",
    sort_order: "0",
    is_active: true,
  });
  const [aiGoal, setAiGoal] = useState("");
  const [aiEntityType, setAiEntityType] = useState("products");
  const [aiResult, setAiResult] = useState(null);

  const products = productsData?.data || [];

console.log("PRODUCT DATA:", products);
  const categories = normalizeCategories(categoriesData);
  const configs = configsData || [];
  const master = masterData || [];
  const sets = Array.isArray(setsData)
  ? setsData
  : setsData?.data || [];
  const brands = brandsData?.data || brandsData || [];
  const getUsedRoles = (setId) => {
  if (!setId) return [];

  return products
    .filter(
      (p) =>
        Number(p.set_id) === Number(setId) &&
        p.is_active
    )
    .map((p) => p.role);
};
const usedRoles = getUsedRoles(productForm.set_id);

  const refresh = () => setReloadKey((k) => k + 1);

  const submitProduct = async () => {
    setBusy(true);
    if (
  productForm.set_id &&
  usedRoles.includes(productForm.kit_role)
) {
  toast(
    `${productForm.kit_role} already exists in this garden kit.`,
    "error"
  );
  setBusy(false);
  return;
}
    try {
      let parsedMetadata = null;
      if ((productForm.metadata_json || "").trim()) {
        try {
          parsedMetadata = JSON.parse(productForm.metadata_json);
          if (
            !parsedMetadata ||
            typeof parsedMetadata !== "object" ||
            Array.isArray(parsedMetadata)
          ) {
            throw new Error("Metadata must be a JSON object");
          }
        } catch {
          throw new Error("Metadata JSON is invalid");
        }
      }

      const payload = {
  name: productForm.name,
  slug: productForm.slug || undefined,
  price: Number(productForm.price),
  stock: Number(productForm.stock || 0),

  category_id: productForm.category_id
    ? Number(productForm.category_id)
    : null,

  set_id: productForm.set_id
    ? Number(productForm.set_id)
    : null,

  brand_id: productForm.brand_id
    ? Number(productForm.brand_id)
    : null,

  description: productForm.description.trim() || null,
  short_desc: productForm.description.trim().slice(0, 500) || null,

  is_featured: Boolean(productForm.is_featured),
  is_active: Boolean(productForm.is_active),

  ai_keywords: productForm.ai_keywords.trim() || null,
  role: productForm.kit_role || null,
};

      const res = await apiFetch(
        "/admin/products",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
        token ? { authToken: token } : {},
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || "Unable to save product");
      const createdProductId = body.id;

if (productImage) {
  await uploadProductImage(productImage, createdProductId);
}
      toast("Product created");
      setProductForm({
  name: "",
  slug: "",
  price: "",
  stock: "0",
  category_id: "",
  set_id: "",
  brand_id: "",
  kit_role: "",
  description: "",
  is_featured: false,
  is_active: true,
  ai_keywords: "",
});
      setProductImage(null);
      refresh();
    } catch (e) {
      toast(e.message || "Failed to create product", "error");
    } finally {
      setBusy(false);
    }
  };

  const deactivateProduct = async (id) => {
    setBusy(true);
    try {
      const res = await apiFetch(
        `/admin/products/${id}`,
        { method: "DELETE" },
        token ? { authToken: token } : {},
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok)
        throw new Error(body.error || "Unable to deactivate product");
      toast("Product archived");
      refresh();
    } catch (e) {
      toast(e.message || "Failed to archive product", "error");
    } finally {
      setBusy(false);
    }
  };
  
const submitCategory = async () => {
  setBusy(true);
  try {
    // 1. create category
    const res = await apiFetch(
      "/admin/categories",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: categoryForm.name,
          slug: categoryForm.slug || undefined,
          sort_order: Number(categoryForm.sort_order || 0),
        }),
      },
      token ? { authToken: token } : {}
    );

    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.error || "Unable to save category");

    const createdCategoryId = body.id;

    let imageUrl = null;

    // 2. upload image
    if (categoryImage) {
      const uploadResult = await uploadCategoryImage(categoryImage, createdCategoryId);
      imageUrl = uploadResult?.url || uploadResult?.imageUrl || null;

      // 3. attach image to category
      if (imageUrl) {
        await apiFetch(
          `/admin/categories/${createdCategoryId}`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ image_url: imageUrl }),
          },
          token ? { authToken: token } : {}
        );
      }
    }

    toast("Category created");

    setCategoryForm({ name: "", slug: "", sort_order: "0" });
    setCategoryImage(null);
    setCategoryImageUrl("");

    refresh();
  } catch (e) {
    toast(e.message || "Failed to create category", "error");
  } finally {
    setBusy(false);
  }
};

  const deleteCategory = async (id) => {
    setBusy(true);
    try {
      const res = await apiFetch(
        `/admin/categories/${id}`,
        { method: "DELETE" },
        token ? { authToken: token } : {},
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || "Unable to delete category");
      toast("Category deleted");
      refresh();
    } catch (e) {
      toast(e.message || "Failed to delete category", "error");
    } finally {
      setBusy(false);
    }
  };

  const submitEditCategory = async () => {
  if (!editingCategory) return;

  setBusy(true);
  try {
    const payload = {
      name: editForm.name.trim(),
      description: editForm.description.trim(),
      is_active: editForm.is_active,
    };

    const res = await apiFetch(
      `/admin/categories/${editingCategory.id}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
      token ? { authToken: token } : {}
    );

    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.error || "Unable to update category");

    toast("Category updated");
    setEditModalOpen(false);
    setEditingCategory(null);
    refresh();
  } catch (e) {
    toast(e.message || "Failed to update category", "error");
  } finally {
    setBusy(false);
  }
};

  const submitSet = async () => {
  setBusy(true);

  try {
    const payload = {
      name: setForm.name,
      category_id: Number(setForm.category_id),
      brand_id: Number(setForm.brand_id),
      total_price: Number(setForm.total_price),
      short_description: setForm.short_description,
      is_featured: setForm.is_featured,
      is_active: setForm.is_active,
      product_ids: setForm.product_ids,
    };

    const res = await apiFetch(
      "/admin/kits",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
      token ? { authToken: token } : {}
    );

    const body = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(
        body.detail ||
        body.error ||
        "Unable to create garden kit"
      );
    }

    const createdSetId = body.id;

    if (setImage && createdSetId) {
      await uploadSetImage(setImage, createdSetId);
    }

    toast("Garden kit created");

    setSetForm({
      name: "",
      category_id: "",
      brand_id: "",
      total_price: "",
      short_description: "",
      is_featured: false,
      is_active: true,
      product_ids: [],
    });

    setSetImage(null);

    refresh();

  } catch (e) {
    toast(e.message || "Failed to create garden kit", "error");
  } finally {
    setBusy(false);
  }
};
const deleteSet = async (id) => {
  setBusy(true);

  try {

    const res = await apiFetch(
      `/admin/kits/${id}`,
      {
        method: "DELETE",
      },
      token ? { authToken: token } : {}
    );

    const body = await res.json().catch(() => ({}));

    if (!res.ok)
      throw new Error(body.error || "Unable to delete set");

    toast("Garden kit deleted");

    refresh();

  } catch (e) {
    toast(e.message || "Delete failed", "error");
  } finally {
    setBusy(false);
  }
};
const editSet = async (set) => {

  const nextName = window.prompt(
    "Edit garden kit name",
    set.name
  );

  if (nextName === null) return;

  if (!nextName.trim()) return;

  setBusy(true);

  try {

    const res = await apiFetch(
      `/admin/kits/${set.id}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: nextName,
        }),
      },
      token ? { authToken: token } : {}
    );

    const body = await res.json().catch(() => ({}));

    if (!res.ok)
      throw new Error(body.error || "Unable to update set");

    toast("Garden kit updated");

    refresh();

  } catch (e) {

    toast(e.message || "Update failed", "error");

  } finally {

    setBusy(false);

  }

};
const submitBrand = async () => {
  setBusy(true);

  try {

    const formData = new FormData();

    formData.append("name", brandForm.name);

    if (brandForm.slug) {
      formData.append("slug", brandForm.slug);
    }

    if (brandForm.description) {
      formData.append("description", brandForm.description);
    }

    if (brandLogo) {
      formData.append("logo", brandLogo);
    }

    const res = await apiFetch(
      "/admin/brands",
      {
        method: "POST",
        body: formData,
      },
      token ? { authToken: token } : {}
    );

    const body = await res.json().catch(() => ({}));

    console.log(body);

    if (!res.ok) {
      throw new Error(
        body.detail ||
        body.message ||
        body.error ||
        "Unable to create brand"
      );
    }

    toast("Brand created");

    setBrandForm({
      name: "",
      slug: "",
      logo_url: "",
      description: "",
    });

    setBrandLogo(null);

    refresh();

  } catch (e) {

    console.error(e);

    toast(e.message, "error");

  } finally {

    setBusy(false);

  }
};
const deleteBrand = async(id)=>{

    setBusy(true);

    try{

        const res = await apiFetch(

            `/admin/brands/${id}`,

            {
                method:"DELETE"
            },

            token ? { authToken: token } : {}

        );

        const body = await res.json().catch(()=>({}));

        if(!res.ok)
            throw new Error(body.error);

        toast("Brand deleted");

        refresh();

    }catch(e){

        toast(e.message,"error");

    }finally{

        setBusy(false);

    }

};
const editBrand = async(brand)=>{

const nextName=window.prompt(

"Edit Brand",

brand.name

);

if(nextName===null) return;

const res=await apiFetch(

`/admin/brands/${brand.id}`,

{

method:"PATCH",

headers:{

"Content-Type":"application/json"

},

body:JSON.stringify({

name:nextName

})

},

token ? { authToken: token } : {}

);

if(res.ok){

toast("Brand updated");

refresh();

}

};

  const submitConfig = async () => {
    setBusy(true);
    try {
      const parsedValue = JSON.parse(configForm.value || "{}");
      const res = await apiFetch(
        `/admin/configurations/${encodeURIComponent(configForm.key)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            value: parsedValue,
            description: configForm.description || "",
            is_sensitive: Boolean(configForm.is_sensitive),
          }),
        },
        token ? { authToken: token } : {},
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok)
        throw new Error(body.error || "Unable to save configuration");
      toast("Configuration saved");
      setConfigForm({
        key: "",
        value: "{}",
        description: "",
        is_sensitive: false,
      });
      refresh();
    } catch (e) {
      toast(e.message || "Failed to save configuration", "error");
    } finally {
      setBusy(false);
    }
  };

  const submitMasterData = async () => {
    setBusy(true);
    try {
      const parsedValue = JSON.parse(masterForm.value_json || "{}");
      const res = await apiFetch(
        "/admin/master-data",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            domain: masterForm.domain,
            key_name: masterForm.key_name,
            value_json: parsedValue,
            description: masterForm.description || "",
            sort_order: Number(masterForm.sort_order || 0),
            is_active: Boolean(masterForm.is_active),
          }),
        },
        token ? { authToken: token } : {},
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || "Unable to save master data");
      toast("Master data record created");
      setMasterForm({
        domain: "",
        key_name: "",
        value_json: "{}",
        description: "",
        sort_order: "0",
        is_active: true,
      });
      refresh();
    } catch (e) {
      toast(e.message || "Failed to save master data", "error");
    } finally {
      setBusy(false);
    }
  };

  const askAI = async () => {
    if (!aiGoal.trim()) {
      toast("Describe what you want the AI assistant to optimize", "error");
      return;
    }

    setBusy(true);
    try {
      const res = await apiFetch(
        "/admin/ai/assist",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            goal: aiGoal,
            entityType: aiEntityType,
            currentData: {
              productCount: products.length,
              categoryCount: categories.length,
              configCount: configs.length,
              masterDataCount: master.length,
            },
          }),
        },
        token ? { authToken: token } : {},
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || "Unable to run AI assistant");
      setAiResult(body);
      toast("AI suggestions ready");
    } catch (e) {
      toast(e.message || "AI request failed", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{ maxWidth: 1240, margin: "0 auto", padding: "42px 24px 80px" }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: 16,
          flexWrap: "wrap",
          marginBottom: 18,
        }}
      >
        <div>
          <p
            style={{
              margin: "0 0 6px",
              fontSize: 12,
              letterSpacing: ".12em",
              textTransform: "uppercase",
              color: adminTheme.gold,
              fontWeight: 700,
            }}
          >
            Admin Workspace
          </p>
          <h1
            style={{
              margin: 0,
              fontSize: 36,
              fontFamily: "'Georgia', serif",
              color: adminTheme.ink,
            }}
          >
            Operations + AI Control Panel
          </h1>
          <p style={{ margin: "10px 0 0", color: adminTheme.muted }}>
            Manage products, categories, system configuration, and master data
            from one secure console.
          </p>
        </div>
        <button
          onClick={() => navigate("/home")}
          style={{
            background: adminTheme.surface,
            border: `2px solid ${adminTheme.gold}`,
            color: adminTheme.ink,
            borderRadius: 12,
            padding: "10px 16px",
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          Exit admin view
        </button>
      </div>

      <div
        style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}
      >
        {[
  "products",
  "categories",
  "sets",
  "brands",
  "configurations",
  "master-data",
  "ai-assistant",
].map((key) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            style={{
              border: `1px solid ${tab === key ? adminTheme.ink : adminTheme.gold}`,
              background: tab === key ? adminTheme.ink : adminTheme.goldSoft,
              color: tab === key ? adminTheme.gold : adminTheme.ink,
              borderRadius: 10,
              padding: "8px 12px",
              fontWeight: 700,
              cursor: "pointer",
              textTransform: "capitalize",
            }}
          >
            {key.replace("-", " ")}
          </button>
        ))}
      </div>

      {tab === "products" && (
        <div
          style={{ display: "grid", gridTemplateColumns: "380px 1fr", gap: 16 }}
        >
          <div
            style={{
              background: "#fff",
              border: `1px solid ${adminTheme.border}`,
              borderRadius: 16,
              padding: 16,
            }}
          >
            <h3 style={{ marginTop: 0, color: adminTheme.ink }}>
              Create Product
            </h3>
           <input
  placeholder="Name"
  value={productForm.name}
  onChange={(e) => {
    const name = e.target.value;

    setProductForm((p) => ({
      ...p,
      name,
      slug: generateSlug(name),
    }));
  }}
  style={{
    width: "100%",
    marginBottom: 10,
    padding: "10px 12px",
    borderRadius: 10,
    border: `1px solid ${adminTheme.border}`,
    background: adminTheme.surface,
    color: adminTheme.ink,
  }}
/>
            <input
              placeholder="Slug (optional)"
              value={productForm.slug}
              onChange={(e) =>
                setProductForm((p) => ({ ...p, slug: e.target.value }))
              }
              style={{
                width: "100%",
                marginBottom: 10,
                padding: "10px 12px",
                borderRadius: 10,
                border: `1px solid ${adminTheme.border}`,
                background: adminTheme.surface,
                color: adminTheme.ink,
              }}
            />
            <input
              type="number"
              min="0"
              placeholder="Price"
              value={productForm.price}
              onChange={(e) =>
                setProductForm((p) => ({ ...p, price: e.target.value }))
              }
              style={{
                width: "100%",
                marginBottom: 10,
                padding: "10px 12px",
                borderRadius: 10,
                border: `1px solid ${adminTheme.border}`,
                background: adminTheme.surface,
                color: adminTheme.ink,
              }}
            />
            <input
              type="number"
              min="0"
              placeholder="Stock"
              value={productForm.stock}
              onChange={(e) =>
                setProductForm((p) => ({ ...p, stock: e.target.value }))
              }
              style={{
                width: "100%",
                marginBottom: 10,
                padding: "10px 12px",
                borderRadius: 10,
                border: `1px solid ${adminTheme.border}`,
                background: adminTheme.surface,
                color: adminTheme.ink,
              }}
            />
            <select
              value={productForm.category_id}
              onChange={(e) =>
                setProductForm((p) => ({ ...p, category_id: e.target.value }))
              }
              style={{
                width: "100%",
                marginBottom: 10,
                padding: "10px 12px",
                borderRadius: 10,
                border: `1px solid ${adminTheme.border}`,
                background: adminTheme.surface,
                color: adminTheme.ink,
              }}
            >
              <option value="">Select Category</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <select
  value={productForm.set_id}
  onChange={(e) =>
    setProductForm((p) => ({
      ...p,
      set_id: e.target.value,
    }))
  }
  style={{
    width: "100%",
    marginBottom: 10,
    padding: "10px 12px",
    borderRadius: 10,
    border: `1px solid ${adminTheme.border}`,
    background: adminTheme.surface,
    color: adminTheme.ink,
  }}
>
  <option value="">Select Garden Kit</option>
  {sets.map((set) => (
    <option
      key={set.id}
      value={set.id}
    >
      {set.name}
    </option>
  ))}
</select>

{productForm.set_id && (() => {
  const progress = getSetProgress(productForm.set_id);

  return (
    <div
      style={{
        marginBottom: 16,
        padding: 12,
        borderRadius: 10,
        border: `1px solid ${adminTheme.border}`,
        background: "#FBFAF8",
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 8 }}>
        Garden Kit Progress
      </div>

      {ROLE_ORDER.map((role) => (
        <div key={role}>
          {progress.roles.includes(role)
            ? "✅"
            : "⬜"}{" "}
          {role}
        </div>
      ))}

      <div
        style={{
          marginTop: 10,
          fontWeight: 700,
        }}
      >
        {progress.completed} / 5 Complete
      </div>

      <div
        style={{
          height: 8,
          background: "#ECE7DF",
          borderRadius: 4,
          marginTop: 8,
        }}
      >
        <div
          style={{
            width: `${(progress.completed / 5) * 100}%`,
            height: "100%",
            background: "#3CB371",
            borderRadius: 4,
          }}
        />
      </div>
    </div>
  );
})()}

{/* Brand selection dropdown */}
<select
  value={productForm.kit_role}
  onChange={(e) =>
    setProductForm((p) => ({
      ...p,
      kit_role: e.target.value,
    }))
  }
  style={{
    width: "100%",
    marginBottom: 10,
    padding: "10px 12px",
    borderRadius: 10,
    border: `1px solid ${adminTheme.border}`,
    background: adminTheme.surface,
    color: adminTheme.ink,
  }}
>
  
  <option value="">Select Kit Role</option>

  {KIT_ROLES.map((role) => {
  const used = usedRoles.includes(role);

  return (
    <option
      key={role}
      value={role}
      disabled={used}
    >
      {role}
      {used ? " ✓ Already Assigned" : ""}
    </option>
  );
})}
</select>
<select
  value={productForm.brand_id}
  onChange={(e) =>
    setProductForm((p) => ({
      ...p,
      brand_id: Number(e.target.value),
    }))
  }
  style={{
    width: "100%",
    marginBottom: 10,
    padding: "10px 12px",
    borderRadius: 10,
    border: `1px solid ${adminTheme.border}`,
    background: adminTheme.surface,
    color: adminTheme.ink,
  }}
>
  <option value="">Select Brand</option>

  {brands.map((brand) => (
    <option key={brand.id} value={brand.id}>
      {brand.name}
    </option>
  ))}
</select>

            <input
              placeholder="AI keywords (comma separated)"
              value={productForm.ai_keywords}
              onChange={(e) =>
                setProductForm((p) => ({ ...p, ai_keywords: e.target.value }))
              }
              style={{
                width: "100%",
                marginBottom: 10,
                padding: "10px 12px",
                borderRadius: 10,
                border: `1px solid ${adminTheme.border}`,
                background: adminTheme.surface,
                color: adminTheme.ink,
              }}
            />
            <div
  style={{
    width: "100%",
    marginBottom: "16px",
    padding: "16px",
    borderRadius: "12px",
    border: `1px solid ${adminTheme.border}`,
    background: adminTheme.surface,
    color: adminTheme.ink,
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  }}
>
  <label
    style={{
      fontWeight: 600,
      fontSize: "14px",
    }}
  >
    Product Image
  </label>

  <input
    type="file"
    accept="image/*"
    onChange={(e) => {
      const file = e.target.files[0];
      setProductImage(file);
    }}
    style={{
      padding: "8px",
      border: `1px solid ${adminTheme.border}`,
      borderRadius: "8px",
      background: adminTheme.bg,
      color: adminTheme.ink,
      cursor: "pointer",
    }}
  />

  {/* Display file path/name */}
  {productImage && (
    <div
      style={{
        fontSize: "13px",
        color: "#888",
        wordBreak: "break-all",
      }}
    >
      Selected file: {productImage.name}
    </div>
  )}

  {/* Image preview */}
  {productImage && (
    <img
      src={URL.createObjectURL(productImage)}
      alt="Preview"
      style={{
        width: "200px",
        maxHeight: "200px",
        objectFit: "cover",
        borderRadius: "10px",
        border: `1px solid ${adminTheme.border}`,
        alignSelf: "flex-start",
      }}
    />
  )}
</div>
            <textarea
  placeholder="Description"
  value={productForm.description}
  onChange={(e) =>
    setProductForm((p) => ({
      ...p,
      description: e.target.value,
    }))
  }
  style={{
    width: "100%",
    minHeight: 120,
    marginBottom: 12,
    padding: "10px 12px",
    borderRadius: 10,
    border: `1px solid ${adminTheme.border}`,
    background: adminTheme.surface,
    color: adminTheme.ink,
    resize: "vertical",
  }}
/>

            <label
              style={{
                display: "flex",
                gap: 8,
                marginBottom: 8,
                color: adminTheme.muted,
              }}
            >
              <input
                type="checkbox"
                checked={productForm.is_featured}
                onChange={(e) =>
                  setProductForm((p) => ({
                    ...p,
                    is_featured: e.target.checked,
                  }))
                }
              />{" "}
              Featured
            </label>
            <label
              style={{
                display: "flex",
                gap: 8,
                marginBottom: 14,
                color: adminTheme.muted,
              }}
            >
              <input
                type="checkbox"
                checked={productForm.is_active}
                onChange={(e) =>
                  setProductForm((p) => ({ ...p, is_active: e.target.checked }))
                }
              />{" "}
              Active
            </label>
            <button
              disabled={busy}
              onClick={submitProduct}
              style={{
                width: "100%",
                border: "none",
                background: adminTheme.ink,
                color: adminTheme.gold,
                borderRadius: 10,
                padding: "10px 14px",
                fontWeight: 700,
                cursor: "pointer",
                opacity: busy ? 0.7 : 1,
              }}
            >
              Save product
            </button>
          </div>

          <div
            style={{
              background: "#fff",
              border: `1px solid ${adminTheme.border}`,
              borderRadius: 16,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "12px 14px",
                borderBottom: `1px solid ${adminTheme.border}`,
                fontWeight: 700,
                color: adminTheme.ink,
              }}
            >
              Catalog ({products.length})
            </div>
            <div style={{ maxHeight: 530, overflow: "auto" }}>
              {products.map((p) => (
                <div
                  key={p.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto auto",
                    gap: 8,
                    padding: "12px 14px",
                    borderBottom: `1px solid ${adminTheme.border}`,
                    alignItems: "center",
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 700, color: adminTheme.ink }}>
                      {p.name}
                    </div>
                    <div style={{ color: adminTheme.muted, fontSize: 12 }}>
                      {p.category_name || "Uncategorized"} · Stock {p.stock} ·{" "}
                      {formatMoney(p.price)}
                    </div>
                  </div>
                  <span
                    style={{
                      fontSize: 12,
                      color: p.is_active ? adminTheme.gold : adminTheme.danger,
                      fontWeight: 700,
                    }}
                  >
                    {p.is_active ? "Active" : "Inactive"}
                  </span>
                  <button
                    disabled={busy || !p.is_active}
                    onClick={() => deactivateProduct(p.id)}
                    style={{
                      border: `1px solid ${adminTheme.border}`,
                      borderRadius: 8,
                      background: adminTheme.surface,
                      color: adminTheme.ink,
                      padding: "6px 8px",
                      cursor: p.is_active ? "pointer" : "default",
                      opacity: p.is_active ? 1 : 0.5,
                    }}
                  >
                    Archive
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "categories" && (
        <div
          style={{ display: "grid", gridTemplateColumns: "380px 1fr", gap: 16 }}
        >
          <div
            style={{
              background: "#fff",
              border: `1px solid ${adminTheme.border}`,
              borderRadius: 16,
              padding: 16,
            }}
          >
            <h3 style={{ marginTop: 0, color: adminTheme.ink }}>
              Create Category
            </h3>
            <input
  placeholder="Name"
  value={categoryForm.name}
  onChange={(e) => {
    const name = e.target.value;

    setCategoryForm((p) => ({
      ...p,
      name,
      slug: generateSlug(name),
    }));
  }}
  style={{
    width: "100%",
    marginBottom: 10,
    padding: "10px 12px",
    borderRadius: 10,
    border: `1px solid ${adminTheme.border}`,
    background: adminTheme.surface,
    color: adminTheme.ink,
  }}
/>
            <input
              placeholder="Slug (optional)"
              value={categoryForm.slug}
              onChange={(e) =>
                setCategoryForm((p) => ({ ...p, slug: e.target.value }))
              }
              style={{
                width: "100%",
                marginBottom: 10,
                padding: "10px 12px",
                borderRadius: 10,
                border: `1px solid ${adminTheme.border}`,
                background: adminTheme.surface,
                color: adminTheme.ink,
              }}
            />
            <input
  type="file"
  accept="image/*"
  onChange={(e) => {
    const file = e.target.files[0];
    setCategoryImage(file);
  }}
  style={{
    width: "100%",
    marginBottom: 10,
    padding: "8px",
    borderRadius: 10,
    border: `1px solid ${adminTheme.border}`,
    background: adminTheme.surface,
  }}
/>

{/* Preview */}
{categoryImage && (
  <img
    src={URL.createObjectURL(categoryImage)}
    alt="preview"
    style={{
      width: "100%",
      maxHeight: 180,
      objectFit: "cover",
      borderRadius: 10,
      marginBottom: 10,
      border: `1px solid ${adminTheme.border}`,
    }}
  />
)}
            <input
              type="number"
              min="0"
              placeholder="Sort order"
              value={categoryForm.sort_order}
              onChange={(e) =>
                setCategoryForm((p) => ({ ...p, sort_order: e.target.value }))
              }
              style={{
                width: "100%",
                marginBottom: 14,
                padding: "10px 12px",
                borderRadius: 10,
                border: `1px solid ${adminTheme.border}`,
                background: adminTheme.surface,
                color: adminTheme.ink,
              }}
            />
            <button
              disabled={busy}
              onClick={submitCategory}
              style={{
                width: "100%",
                border: "none",
                background: adminTheme.ink,
                color: adminTheme.gold,
                borderRadius: 10,
                padding: "10px 14px",
                fontWeight: 700,
                cursor: "pointer",
                opacity: busy ? 0.7 : 1,
              }}
            >
              Save category
            </button>
          </div>
          <div
            style={{
              background: "#fff",
              border: `1px solid ${adminTheme.border}`,
              borderRadius: 16,
              maxHeight: 530,
              overflow: "auto",
            }}
          >
            <div
              style={{
                padding: "12px 14px",
                borderBottom: `1px solid ${adminTheme.border}`,
                fontWeight: 700,
                color: adminTheme.ink,
              }}
            >
              Categories ({categories.length})
            </div>
            {categories.map((c) => (
              <div
                key={c.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto auto",
                  gap: 8,
                  padding: "12px 14px",
                  borderBottom: `1px solid ${adminTheme.border}`,
                  alignItems: "center",
                }}
              >
                <div>
                  <div style={{ fontWeight: 700, color: adminTheme.ink }}>
                    {c.name}
                  </div>
                  <div style={{ color: adminTheme.muted, fontSize: 12 }}>
                    {c.slug} · Sort {c.sort_order}
                  </div>
                </div>
                <button
                  disabled={busy}
                  onClick={() => openEditCategory(c)}
                  style={{
                    border: `1px solid ${adminTheme.gold}`,
                    color: adminTheme.ink,
                    borderRadius: 8,
                    background: adminTheme.goldSoft,
                    padding: "6px 10px",
                    cursor: busy ? "default" : "pointer",
                    opacity: busy ? 0.7 : 1,
                  }}
                >
                  Edit
                </button>
                
                <button
                  disabled={busy}
                  onClick={() => deleteCategory(c.id)}
                  style={{
                    border: "1px solid #e5b9b5",
                    color: "#a94442",
                    borderRadius: 8,
                    background: "#fff",
                    padding: "6px 10px",
                    cursor: busy ? "default" : "pointer",
                    opacity: busy ? 0.7 : 1,
                  }}
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      {editModalOpen && editingCategory && (
  <DraggableModal
    title="Edit Category"
    onClose={() => setEditModalOpen(false)}
  >
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr",
        gap: 10,
      }}
    >
      {/* NAME */}
      <input
        placeholder="Name"
        value={editForm.name}
        onChange={(e) =>
          setEditForm((p) => ({ ...p, name: e.target.value }))
        }
        style={inputStyle}
      />

      {/* SLUG */}
      <input
        placeholder="Slug"
        value={editForm.slug}
        onChange={(e) =>
          setEditForm((p) => ({ ...p, slug: e.target.value }))
        }
        style={inputStyle}
      />

      {/* IMAGE */}
      <input
        type="file"
        accept="image/*"
        onChange={(e) => {
          const file = e.target.files[0];
          setEditForm((p) => ({ ...p, image: file }));
        }}
        style={inputStyle}
      />

      {/* SORT ORDER */}
      <input
        type="number"
        placeholder="Sort order"
        value={editForm.sort_order}
        onChange={(e) =>
          setEditForm((p) => ({
            ...p,
            sort_order: e.target.value,
          }))
        }
        style={inputStyle}
      />

      {/* BUTTONS */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <button onClick={() => setEditModalOpen(false)}>
          Cancel
        </button>

        <button onClick={submitEditCategory} disabled={busy}>
          Save
        </button>
      </div>
    </div>
  </DraggableModal>
)}
{tab === "sets" && (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "400px 1fr",
      gap: 16,
    }}
  >
    {/* LEFT FORM PANEL */}
    <div
      style={{
        background: "#fff",
        border: `1px solid ${adminTheme.border}`,
        borderRadius: 16,
        padding: 18,
      }}
    >
      <h3
        style={{
          marginTop: 0,
          marginBottom: 18,
          color: adminTheme.ink,
        }}
      >
        Create Garden Kit
      </h3>

      {/* SET NAME */}
      <input
        placeholder="Garden Kit Name"
        value={setForm.name}
        onChange={(e) =>
          setSetForm((p) => ({
            ...p,
            name: e.target.value,
          }))
        }
        style={{
          width: "100%",
          marginBottom: 12,
          padding: "10px 12px",
          borderRadius: 10,
          border: `1px solid ${adminTheme.border}`,
          background: adminTheme.surface,
          color: adminTheme.ink,
        }}
      />

      {/* CATEGORY */}
      <select
        value={setForm.category_id}
        onChange={(e) =>
          setSetForm((p) => ({
            ...p,
            category_id: e.target.value,
          }))
        }
        style={{
          width: "100%",
          marginBottom: 12,
          padding: "10px 12px",
          borderRadius: 10,
          border: `1px solid ${adminTheme.border}`,
          background: adminTheme.surface,
        }}
      >
        <option value="">Select Category</option>
        {categories.map((category) => (
          <option key={category.id} value={category.id}>
            {category.name}
          </option>
        ))}
      </select>

      {/* BRAND */}
      <select
        value={setForm.brand_id}
        onChange={(e) =>
          setSetForm((p) => ({
            ...p,
            brand_id: e.target.value,
          }))
        }
        style={{
          width: "100%",
          marginBottom: 12,
          padding: "10px 12px",
          borderRadius: 10,
          border: `1px solid ${adminTheme.border}`,
          background: adminTheme.surface,
        }}
      >
        <option value="">Select Brand</option>
        {brands.map((brand) => (
          <option key={brand.id} value={brand.id}>
            {brand.name}
          </option>
        ))}
      </select>

      {/* TOTAL PRICE */}
      <input
        type="number"
        min="0"
        placeholder="Total Price"
        value={setForm.total_price}
        onChange={(e) =>
          setSetForm((p) => ({
            ...p,
            total_price: e.target.value,
          }))
        }
        style={{
          width: "100%",
          marginBottom: 12,
          padding: "10px 12px",
          borderRadius: 10,
          border: `1px solid ${adminTheme.border}`,
          background: adminTheme.surface,
        }}
      />
      {/* SHORT DESCRIPTION */}
      <textarea
  placeholder="Short Description"
  value={setForm.short_description}
  onChange={(e) =>
    setSetForm((p) => ({
      ...p,
      short_description: e.target.value,
    }))
  }
  maxLength={120}
  style={{
    width: "100%",
    minHeight: 70,
    marginBottom: 14,
    padding: "10px 12px",
    borderRadius: 10,
    border: `1px solid ${adminTheme.border}`,
    background: adminTheme.surface,
    color: adminTheme.ink,
    resize: "vertical",
  }}
/>

<div
  style={{
    textAlign: "right",
    fontSize: 12,
    color: adminTheme.muted,
    marginTop: -8,
    marginBottom: 14,
  }}
>
  {setForm.short_description.length}/120 characters
</div>

      {/* IMAGE UPLOAD */}
      <div
        style={{
          marginBottom: 18,
          padding: 14,
          borderRadius: 12,
          border: `1px solid ${adminTheme.border}`,
          background: adminTheme.surface,
        }}
      >
        <div
          style={{
            fontWeight: 700,
            marginBottom: 12,
            color: adminTheme.ink,
          }}
        >
          Kit Cover Image
        </div>

        <input
          type="file"
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) setSetImage(file);
          }}
        />

        {setImage && (
          <img
            src={URL.createObjectURL(setImage)}
            alt="preview"
            style={{
              marginTop: 12,
              width: "100%",
              maxHeight: 220,
              objectFit: "cover",
              borderRadius: 10,
              border: `1px solid ${adminTheme.border}`,
            }}
          />
        )}
      </div>
      <label
  style={{
    display: "flex",
    gap: 10,
    marginBottom: 10,
    color: adminTheme.muted,
  }}
>
  <input
    type="checkbox"
    checked={setForm.is_featured}
    onChange={(e) =>
      setSetForm((p) => ({
        ...p,
        is_featured: e.target.checked,
      }))
    }
  />
  Featured
</label>
<label
  style={{
    display: "flex",
    gap: 10,
    marginBottom: 18,
    color: adminTheme.muted,
  }}
>
  <input
    type="checkbox"
    checked={setForm.is_active}
    onChange={(e) =>
      setSetForm((p) => ({
        ...p,
        is_active: e.target.checked,
      }))
    }
  />
  Active
</label>
      {/* SUBMIT */}
      <button
        disabled={busy}
        onClick={submitSet}
        style={{
          width: "100%",
          border: "none",
          background: adminTheme.ink,
          color: adminTheme.gold,
          borderRadius: 10,
          padding: "12px 14px",
          fontWeight: 700,
          cursor: "pointer",
          opacity: busy ? 0.7 : 1,
        }}
      >
        Save Garden Kit
      </button>
    </div>

    {/* RIGHT SIDE  */}
    <div
  style={{
    background: "#fff",
    border: `1px solid ${adminTheme.border}`,
    borderRadius: 16,
    overflow: "hidden",
  }}
>
  <div
  style={{
    padding: "14px 18px",
    borderBottom: `1px solid ${adminTheme.border}`,
    fontWeight: 700,
    color: adminTheme.ink,
    fontSize: 18,
  }}
>
  Garden Kits ({sets.length})
</div>
{sets.length === 0 && (
  <div
    style={{
      padding: 80,
      textAlign: "center",
      color: adminTheme.muted,
    }}
  >
    <div
      style={{
        fontSize: 54,
        marginBottom: 16,
      }}
    >
      👕
    </div>

    <h3
      style={{
        margin: 0,
        color: adminTheme.ink,
      }}
    >
      No Garden Kits Yet
    </h3>

    <p
      style={{
        marginTop: 10,
      }}
    >
      Create your first complete garden kit.
    </p>
  </div>
)}
<div
  style={{
    maxHeight: 650,
    overflowY: "auto",
  }}
>
 {sets.map((set) => (
  <div
    key={set.id}
    style={{
      display: "grid",
      gridTemplateColumns: "90px 1fr auto",
      gap: 12,
      padding: "12px 14px",
      borderBottom: `1px solid ${adminTheme.border}`,
      alignItems: "center",
    }}
  >
    {/* IMAGE (SMALLER BUT VISIBLE) */}
    <img
      src={
        set.image_url ||
        "https://via.placeholder.com/90x90?text=Kit"
      }
      alt={set.name}
      style={{
        width: 90,
        height: 90,
        objectFit: "cover",
        borderRadius: 12,
        border: `1px solid ${adminTheme.border}`,
      }}
    />

    {/* DETAILS */}
    <div>
      <div
        style={{
          fontWeight: 700,
          fontSize: 16,
          color: adminTheme.ink,
        }}
      >
        {set.name}
      </div>

      {/* SINGLE LINE META (like Brands UI style) */}
      <div
        style={{
          marginTop: 6,
          color: adminTheme.muted,
          fontSize: 12,
        }}
      >
        {set.category_name} · {set.brand_name} · {formatMoney(set.total_price)}
      </div>

      {/* TAGS ONLY */}
      <div
        style={{
          display: "flex",
          gap: 8,
          marginTop: 10,
          flexWrap: "wrap",
        }}
      >
        {set.is_featured && (
          <span
            style={{
              background: adminTheme.goldSoft,
              color: adminTheme.gold,
              padding: "3px 8px",
              borderRadius: 20,
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            Featured
          </span>
        )}

        <span
          style={{
            background: set.is_active ? "#EEF8EE" : "#FDEEEE",
            color: set.is_active ? "#2E7D32" : "#C62828",
            padding: "3px 8px",
            borderRadius: 20,
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          {set.is_active ? "Active" : "Inactive"}
        </span>
      </div>
    </div>

    {/* ACTIONS */}
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <button
        disabled={busy}
        onClick={() => editSet(set)}
        style={{
          border: `1px solid ${adminTheme.gold}`,
          background: adminTheme.goldSoft,
          color: adminTheme.ink,
          borderRadius: 8,
          padding: "6px 10px",
          fontWeight: 700,
          cursor: "pointer",
          fontSize: 12,
        }}
      >
        Edit
      </button>

      <button
        disabled={busy}
        onClick={() => deleteSet(set.id)}
        style={{
          border: "1px solid #e5b9b5",
          background: "#fff",
          color: "#B23A3A",
          borderRadius: 8,
          padding: "6px 10px",
          fontWeight: 700,
          cursor: "pointer",
          fontSize: 12,
        }}
      >
        Delete
      </button>
    </div>
  </div>
))}
</div>
    </div>
  </div>
)}

{/* BRANDS TAB */}
{tab === "brands" && (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "400px 1fr",
      gap: 16,
    }}
  >
    {/* LEFT FORM PANEL */}
<div
  style={{
    background: "#fff",
    border: `1px solid ${adminTheme.border}`,
    borderRadius: 16,
    padding: 18,
  }}
>
  <h3
    style={{
      marginTop: 0,
      marginBottom: 18,
      color: adminTheme.ink,
    }}
  >
    Create Brand
  </h3>

  {/* BRAND NAME */}
  <input
    placeholder="Brand Name"
    value={brandForm.name}
    onChange={(e) => {
      const name = e.target.value;

      setBrandForm((p) => ({
        ...p,
        name,
        slug: generateSlug(name),
      }));
    }}
    style={{
      width: "100%",
      marginBottom: 12,
      padding: "10px 12px",
      borderRadius: 10,
      border: `1px solid ${adminTheme.border}`,
      background: adminTheme.surface,
      color: adminTheme.ink,
    }}
  />

  {/* SLUG */}
  <input
    placeholder="Slug"
    value={brandForm.slug}
    onChange={(e) =>
      setBrandForm((p) => ({
        ...p,
        slug: e.target.value,
      }))
    }
    style={{
      width: "100%",
      marginBottom: 12,
      padding: "10px 12px",
      borderRadius: 10,
      border: `1px solid ${adminTheme.border}`,
      background: adminTheme.surface,
      color: adminTheme.ink,
    }}
  />

  {/* LOGO UPLOAD */}
  <div
    style={{
      marginBottom: 18,
      padding: 14,
      borderRadius: 12,
      border: `1px solid ${adminTheme.border}`,
      background: adminTheme.surface,
    }}
  >
    <div
  style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  }}
>
  <div
    style={{
      fontWeight: 700,
      color: adminTheme.ink,
    }}
  >
    Brand Logo
  </div>

  <span
    style={{
      fontSize: 12,
      color: adminTheme.muted,
      fontStyle: "italic",
    }}
  >
    Optional
  </span>
</div>

    <input
      type="file"
      accept="image/*"
      onChange={(e) => {
        const file = e.target.files?.[0];

        if (file) {
          setBrandLogo(file);
        }
      }}
    />

    {brandLogo && (
      <>
        <div
          style={{
            marginTop: 10,
            fontSize: 13,
            color: adminTheme.muted,
          }}
        >
          {brandLogo.name}
        </div>

        <img
          src={URL.createObjectURL(brandLogo)}
          alt="Brand Preview"
          style={{
            marginTop: 12,
            width: "100%",
            maxHeight: 220,
            objectFit: "contain",
            borderRadius: 10,
            border: `1px solid ${adminTheme.border}`,
            background: "#fff",
          }}
        />
      </>
    )}
  </div>

  {/* DESCRIPTION */}
  <textarea
    placeholder="Brand Description"
    value={brandForm.description}
    onChange={(e) =>
      setBrandForm((p) => ({
        ...p,
        description: e.target.value,
      }))
    }
    maxLength={250}
    style={{
      width: "100%",
      minHeight: 110,
      marginBottom: 8,
      padding: "10px 12px",
      borderRadius: 10,
      border: `1px solid ${adminTheme.border}`,
      background: adminTheme.surface,
      color: adminTheme.ink,
      resize: "vertical",
    }}
  />

  <div
    style={{
      textAlign: "right",
      color: adminTheme.muted,
      fontSize: 12,
      marginBottom: 18,
    }}
  >
    {brandForm.description.length}/250 characters
  </div>

  {/* SAVE BUTTON */}
  <button
    disabled={busy}
    onClick={submitBrand}
    style={{
      width: "100%",
      border: "none",
      background: adminTheme.ink,
      color: adminTheme.gold,
      borderRadius: 10,
      padding: "12px 14px",
      fontWeight: 700,
      cursor: "pointer",
      opacity: busy ? 0.7 : 1,
      transition: ".25s",
    }}
  >
    {busy ? "Saving..." : "Save Brand"}
  </button>
</div>

    {/* RIGHT PANEL */}
<div
  style={{
    background: "#fff",
    border: `1px solid ${adminTheme.border}`,
    borderRadius: 16,
    overflow: "hidden",
  }}
>
  <div
    style={{
      padding: "14px 18px",
      borderBottom: `1px solid ${adminTheme.border}`,
      fontWeight: 700,
      color: adminTheme.ink,
      fontSize: 18,
    }}
  >
    Brands ({brands.length})
  </div>

  {/* EMPTY STATE */}
  {brands.length === 0 && (
    <div
      style={{
        padding: 80,
        textAlign: "center",
        color: adminTheme.muted,
      }}
    >
      <div
        style={{
          fontSize: 56,
          marginBottom: 18,
        }}
      >
        🏷️
      </div>

      <h3
        style={{
          margin: 0,
          color: adminTheme.ink,
        }}
      >
        No Brands Yet
      </h3>

      <p
        style={{
          marginTop: 10,
        }}
      >
        Create your first brand.
      </p>
    </div>
  )}

 {/* BRAND LIST */}
{brands.length > 0 && (
  <div
    style={{
      maxHeight: 530,
      overflowY: "auto",
    }}
  >
    {brands.map((brand) => (
      <div
        key={brand.id}
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto auto",
          gap: 8,
          padding: "12px 14px",
          borderBottom: `1px solid ${adminTheme.border}`,
          alignItems: "center",
        }}
      >
        <div
  style={{
    display: "flex",
    alignItems: "center",
    gap: 12,
  }}
>
  {/* Brand Logo */}
  <div
    style={{
      width: 46,
      height: 46,
      borderRadius: 10,
      overflow: "hidden",
      border: `1px solid ${adminTheme.border}`,
      background: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}
  >
    {brand.logo_url ? (
      <img
        src={brand.logo_url}
        alt={brand.name}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
        }}
      />
    ) : (
      <span style={{ fontSize: 20 }}>🏷️</span>
    )}
  </div>

  {/* Brand Details */}
  <div>
    <div
      style={{
        fontWeight: 700,
        color: adminTheme.ink,
      }}
    >
      {brand.name}
    </div>

    <div
      style={{
        color: adminTheme.muted,
        fontSize: 12,
      }}
    >
      {brand.slug} · Created{" "}
      {brand.created_at
        ? new Date(brand.created_at).toLocaleDateString()
        : "-"}
    </div>
  </div>
</div>

        <button
          disabled={busy}
          onClick={() => editBrand(brand)}
          style={{
            border: `1px solid ${adminTheme.gold}`,
            color: adminTheme.ink,
            borderRadius: 8,
            background: adminTheme.goldSoft,
            padding: "6px 10px",
            cursor: busy ? "default" : "pointer",
            opacity: busy ? 0.7 : 1,
          }}
        >
          Edit
        </button>

        <button
          disabled={busy}
          onClick={() => deleteBrand(brand.id)}
          style={{
            border: "1px solid #e5b9b5",
            color: "#a94442",
            borderRadius: 8,
            background: "#fff",
            padding: "6px 10px",
            cursor: busy ? "default" : "pointer",
            opacity: busy ? 0.7 : 1,
          }}
        >
          Delete
        </button>
      </div>
    ))}
  </div>
)}
</div>
  </div>
)}
      {tab === "configurations" && (
        <div
          style={{ display: "grid", gridTemplateColumns: "420px 1fr", gap: 16 }}
        >
          <div
            style={{
              background: "#fff",
              border: `1px solid ${adminTheme.border}`,
              borderRadius: 16,
              padding: 16,
            }}
          >
            <h3 style={{ marginTop: 0, color: adminTheme.ink }}>
              Upsert Configuration
            </h3>
            <input
              placeholder="Config key"
              value={configForm.key}
              onChange={(e) =>
                setConfigForm((p) => ({ ...p, key: e.target.value }))
              }
              style={{
                width: "100%",
                marginBottom: 10,
                padding: "10px 12px",
                borderRadius: 10,
                border: `1px solid ${adminTheme.border}`,
                background: adminTheme.surface,
                color: adminTheme.ink,
              }}
            />
            <textarea
              placeholder='JSON value, e.g. {"feature": true}'
              value={configForm.value}
              onChange={(e) =>
                setConfigForm((p) => ({ ...p, value: e.target.value }))
              }
              style={{
                width: "100%",
                minHeight: 120,
                marginBottom: 10,
                padding: "10px 12px",
                borderRadius: 10,
                border: `1px solid ${adminTheme.border}`,
                background: adminTheme.surface,
                color: adminTheme.ink,
                fontFamily: "monospace",
              }}
            />
            <input
              placeholder="Description"
              value={configForm.description}
              onChange={(e) =>
                setConfigForm((p) => ({ ...p, description: e.target.value }))
              }
              style={{
                width: "100%",
                marginBottom: 10,
                padding: "10px 12px",
                borderRadius: 10,
                border: `1px solid ${adminTheme.border}`,
                background: adminTheme.surface,
                color: adminTheme.ink,
              }}
            />
            <label
              style={{
                display: "flex",
                gap: 8,
                marginBottom: 14,
                color: adminTheme.muted,
              }}
            >
              <input
                type="checkbox"
                checked={configForm.is_sensitive}
                onChange={(e) =>
                  setConfigForm((p) => ({
                    ...p,
                    is_sensitive: e.target.checked,
                  }))
                }
              />{" "}
              Sensitive
            </label>
            <button
              disabled={busy}
              onClick={submitConfig}
              style={{
                width: "100%",
                border: "none",
                background: adminTheme.ink,
                color: adminTheme.gold,
                borderRadius: 10,
                padding: "10px 14px",
                fontWeight: 700,
                cursor: "pointer",
                opacity: busy ? 0.7 : 1,
              }}
            >
              Save configuration
            </button>
          </div>
          <div
            style={{
              background: "#fff",
              border: `1px solid ${adminTheme.border}`,
              borderRadius: 16,
              maxHeight: 530,
              overflow: "auto",
            }}
          >
            <div
              style={{
                padding: "12px 14px",
                borderBottom: `1px solid ${adminTheme.border}`,
                fontWeight: 700,
                color: adminTheme.ink,
              }}
            >
              Configuration Store ({configs.length})
            </div>
            {configs.map((cfg) => (
              <div
                key={cfg.id}
                style={{
                  padding: "12px 14px",
                  borderBottom: `1px solid ${adminTheme.border}`,
                }}
              >
                <div style={{ fontWeight: 700, color: adminTheme.ink }}>
                  {cfg.key}
                </div>
                <div
                  style={{
                    color: adminTheme.muted,
                    fontSize: 12,
                    marginTop: 4,
                  }}
                >
                  {cfg.description || "No description"}
                </div>
                <pre
                  style={{
                    margin: "8px 0 0",
                    background: adminTheme.surface,
                    border: `1px solid ${adminTheme.border}`,
                    borderRadius: 8,
                    padding: 8,
                    fontSize: 12,
                    overflowX: "auto",
                  }}
                >
                  {typeof cfg.value === "string"
                    ? cfg.value
                    : JSON.stringify(cfg.value, null, 2)}
                </pre>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "master-data" && (
        <div
          style={{ display: "grid", gridTemplateColumns: "420px 1fr", gap: 16 }}
        >
          <div
            style={{
              background: "#fff",
              border: `1px solid ${adminTheme.border}`,
              borderRadius: 16,
              padding: 16,
            }}
          >
            <h3 style={{ marginTop: 0, color: adminTheme.ink }}>
              Create Master Data Record
            </h3>
            <input
              placeholder="Domain"
              value={masterForm.domain}
              onChange={(e) =>
                setMasterForm((p) => ({ ...p, domain: e.target.value }))
              }
              style={{
                width: "100%",
                marginBottom: 10,
                padding: "10px 12px",
                borderRadius: 10,
                border: `1px solid ${adminTheme.border}`,
                background: adminTheme.surface,
                color: adminTheme.ink,
              }}
            />
            <input
              placeholder="Key"
              value={masterForm.key_name}
              onChange={(e) =>
                setMasterForm((p) => ({ ...p, key_name: e.target.value }))
              }
              style={{
                width: "100%",
                marginBottom: 10,
                padding: "10px 12px",
                borderRadius: 10,
                border: `1px solid ${adminTheme.border}`,
                background: adminTheme.surface,
                color: adminTheme.ink,
              }}
            />
            <textarea
              placeholder='JSON value, e.g. {"label": "Default"}'
              value={masterForm.value_json}
              onChange={(e) =>
                setMasterForm((p) => ({ ...p, value_json: e.target.value }))
              }
              style={{
                width: "100%",
                minHeight: 120,
                marginBottom: 10,
                padding: "10px 12px",
                borderRadius: 10,
                border: `1px solid ${adminTheme.border}`,
                background: adminTheme.surface,
                color: adminTheme.ink,
                fontFamily: "monospace",
              }}
            />
            <input
              placeholder="Description"
              value={masterForm.description}
              onChange={(e) =>
                setMasterForm((p) => ({ ...p, description: e.target.value }))
              }
              style={{
                width: "100%",
                marginBottom: 10,
                padding: "10px 12px",
                borderRadius: 10,
                border: `1px solid ${adminTheme.border}`,
                background: adminTheme.surface,
                color: adminTheme.ink,
              }}
            />
            <input
              type="number"
              placeholder="Sort order"
              value={masterForm.sort_order}
              onChange={(e) =>
                setMasterForm((p) => ({ ...p, sort_order: e.target.value }))
              }
              style={{
                width: "100%",
                marginBottom: 10,
                padding: "10px 12px",
                borderRadius: 10,
                border: `1px solid ${adminTheme.border}`,
                background: adminTheme.surface,
                color: adminTheme.ink,
              }}
            />
            <label
              style={{
                display: "flex",
                gap: 8,
                marginBottom: 14,
                color: adminTheme.muted,
              }}
            >
              <input
                type="checkbox"
                checked={masterForm.is_active}
                onChange={(e) =>
                  setMasterForm((p) => ({ ...p, is_active: e.target.checked }))
                }
              />{" "}
              Active
            </label>
            <button
              disabled={busy}
              onClick={submitMasterData}
              style={{
                width: "100%",
                border: "none",
                background: adminTheme.ink,
                color: adminTheme.gold,
                borderRadius: 10,
                padding: "10px 14px",
                fontWeight: 700,
                cursor: "pointer",
                opacity: busy ? 0.7 : 1,
              }}
            >
              Save master data
            </button>
          </div>
          <div
            style={{
              background: "#fff",
              border: `1px solid ${adminTheme.border}`,
              borderRadius: 16,
              maxHeight: 530,
              overflow: "auto",
            }}
          >
            <div
              style={{
                padding: "12px 14px",
                borderBottom: `1px solid ${adminTheme.border}`,
                fontWeight: 700,
                color: adminTheme.ink,
              }}
            >
              Master Data ({master.length})
            </div>
            {master.map((entry) => (
              <div
                key={entry.id}
                style={{
                  padding: "12px 14px",
                  borderBottom: `1px solid ${adminTheme.border}`,
                }}
              >
                <div style={{ fontWeight: 700, color: adminTheme.ink }}>
                  {entry.domain} · {entry.key_name}
                </div>
                <div
                  style={{
                    color: adminTheme.muted,
                    fontSize: 12,
                    marginTop: 4,
                  }}
                >
                  {entry.description || "No description"}
                </div>
                <pre
                  style={{
                    margin: "8px 0 0",
                    background: adminTheme.surface,
                    border: `1px solid ${adminTheme.border}`,
                    borderRadius: 8,
                    padding: 8,
                    fontSize: 12,
                    overflowX: "auto",
                  }}
                >
                  {JSON.stringify(entry.value_json, null, 2)}
                </pre>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "ai-assistant" && (
        <div
          style={{ display: "grid", gridTemplateColumns: "420px 1fr", gap: 16 }}
        >
          <div
            style={{
              background: "#fff",
              border: `1px solid ${adminTheme.border}`,
              borderRadius: 16,
              padding: 16,
            }}
          >
            <h3 style={{ marginTop: 0, color: adminTheme.ink }}>
              AI Admin Assistant
            </h3>
            <select
              value={aiEntityType}
              onChange={(e) => setAiEntityType(e.target.value)}
              style={{
                width: "100%",
                marginBottom: 10,
                padding: "10px 12px",
                borderRadius: 10,
                border: `1px solid ${adminTheme.border}`,
                background: adminTheme.surface,
                color: adminTheme.ink,
              }}
            >
              <option value="products">Products</option>
              <option value="categories">Categories</option>
              <option value="configurations">Configurations</option>
              <option value="master-data">Master data</option>
            </select>
            <textarea
              value={aiGoal}
              onChange={(e) => setAiGoal(e.target.value)}
              placeholder="Example: Suggest a safer structure for shipping-related configurations and identify stale values."
              style={{
                width: "100%",
                minHeight: 150,
                marginBottom: 14,
                padding: "10px 12px",
                borderRadius: 10,
                border: `1px solid ${adminTheme.border}`,
                background: adminTheme.surface,
                color: adminTheme.ink,
                lineHeight: 1.5,
              }}
            />
            <button
              disabled={busy}
              onClick={askAI}
              style={{
                width: "100%",
                border: "none",
                background: adminTheme.ink,
                color: adminTheme.gold,
                borderRadius: 10,
                padding: "10px 14px",
                fontWeight: 700,
                cursor: "pointer",
                opacity: busy ? 0.7 : 1,
              }}
            >
              Run AI analysis
            </button>
          </div>
          <div
            style={{
              background: "#fff",
              border: `1px solid ${adminTheme.border}`,
              borderRadius: 16,
              padding: 16,
            }}
          >
            <h3 style={{ marginTop: 0, color: adminTheme.ink }}>Suggestions</h3>
            {!aiResult ? (
              <p style={{ color: adminTheme.muted }}>
                Run the assistant to generate recommendations.
              </p>
            ) : (
              <>
                <p style={{ color: adminTheme.muted, marginTop: 0 }}>
                  Provider: <strong>{aiResult.provider || "unknown"}</strong>
                  {aiResult.model ? ` (${aiResult.model})` : ""}
                </p>
                {aiResult.summary && (
                  <p style={{ color: adminTheme.ink }}>{aiResult.summary}</p>
                )}
                <div style={{ display: "grid", gap: 10 }}>
                  {(aiResult.suggestions || []).map((item, idx) => (
                    <div
                      key={idx}
                      style={{
                        border: `1px solid ${adminTheme.border}`,
                        borderRadius: 10,
                        padding: "10px 12px",
                        background: adminTheme.surface,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 8,
                        }}
                      >
                        <strong style={{ color: adminTheme.ink }}>
                          {item.title || "Recommendation"}
                        </strong>
                        <span style={{ color: adminTheme.muted, fontSize: 12 }}>
                          {Math.round(Number(item.confidence || 0.7) * 100)}%
                        </span>
                      </div>
                      <p style={{ margin: "8px 0 0", color: adminTheme.muted }}>
                        {item.detail}
                      </p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
